import useQuery from 'hooks/react-query/useQuery';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { DiscountType } from 'types/subscriptions.types';
import formatMoney from 'utils/formatMoney';
import getPromotions, {
  type PromotionFromAPI,
} from 'api/v1/subscriptions/getPromotions';
import { SubscriptionsCacheKeys } from 'types/cacheKeys.types';
import type { AvailableCountries } from 'types/users.types';
import useCustomerCountry from 'hooks/shared/useCustomerCountry';

/** `useTranslation().t` from react-i18next (i18next v25 `TFunction` is non-generic here) */
type TranslationFn = TFunction;

/** Discounts keyed by plan internal name (e.g. eu.accountable.basic1) */
export type DiscountsByPlan = Record<string, DiscountType>;

function formatDiscountTranslation(
  discount: DiscountType,
  translationKey: string,
  t: TranslationFn,
): string {
  const raw = discount.promotionDiscountValueRaw;

  if (discount.discountType === 'percentage') {
    return t(translationKey, { percent: raw });
  }

  if (discount.discountType === 'fixed') {
    return t(translationKey, { amount: formatMoney(raw) });
  }

  return t(translationKey, {
    amount: formatMoney(raw),
    count: raw,
  });
}

function compareDiscounts(a: DiscountType, b: DiscountType): number {
  if (a.discountType !== b.discountType) {
    const rank = { percentage: 3, fixed: 2 };
    return rank[a.discountType] - rank[b.discountType];
  }
  return a.promotionDiscountValueRaw - b.promotionDiscountValueRaw;
}

function getTabTranslationKey(
  discount: DiscountType,
  installment: 1 | 12,
): string | null | undefined {
  return installment === 12
    ? discount.translationKeyYearlyTab
    : discount.translationKeyMonthlyTab;
}

export function getTabBadgeTextForInstallment(options: {
  installment: 1 | 12;
  planIds: string[];
  discountsByPlan: DiscountsByPlan | undefined;
  t: TranslationFn;
}): string | undefined {
  const { installment, planIds, discountsByPlan, t } = options;

  if (!discountsByPlan) return undefined;

  const discountsForEachPlan = planIds.map((id) => discountsByPlan?.[id]);

  const withDiscount = discountsForEachPlan.filter(
    (d): d is DiscountType => d != null,
  );

  if (withDiscount.length === 0) return undefined;

  const everyPlanHasDiscount = discountsForEachPlan.every((d) => d != null);

  const allSameCoupon =
    everyPlanHasDiscount &&
    withDiscount.every((d) => d.couponId === withDiscount[0].couponId);

  if (allSameCoupon) {
    const d = withDiscount[0];

    const tabKey = getTabTranslationKey(d, installment);

    if (!tabKey) return undefined;

    return formatDiscountTranslation(d, tabKey, t);
  }

  const best = withDiscount.reduce((winner, d) =>
    compareDiscounts(d, winner) > 0 ? d : winner,
  );

  const tabKey = getTabTranslationKey(best, installment);

  if (!tabKey) return undefined;

  return formatDiscountTranslation(best, tabKey, t);
}

function mapPromotionToDiscount(
  promo: PromotionFromAPI,
  t: TranslationFn,
): DiscountType {
  const isPercentage = promo.discountType === 'percentage';
  const isFixed = promo.discountType === 'fixed';

  const discountValue = isPercentage
    ? promo.discountValue / 100
    : promo.discountValue;

  const discountText = isPercentage
    ? t(promo.translationKeyDiscountText, { percent: promo.discountValue })
    : isFixed
    ? t(promo.translationKeyDiscountText, {
        amount: formatMoney(promo.discountValue),
      })
    : t(promo.translationKeyDiscountText, {
        amount: formatMoney(promo.discountValue),
        count: promo.discountValue,
      });

  const planWidgetKey = promo.translationKeyPlanWidget;

  const planWidgetDiscountText = planWidgetKey
    ? (originalAmount: number) =>
        t(planWidgetKey, {
          original_amount: formatMoney(originalAmount, 'EUR'),
        })
    : undefined;

  const helper =
    promo.translationKeyHelperTitle || promo.translationKeyHelperContent
      ? {
          title: t(promo.translationKeyHelperTitle),
          content: promo.translationKeyHelperContent
            ? t(promo.translationKeyHelperContent)
            : '',
        }
      : undefined;

  return {
    couponId: promo.couponId,
    discountType: promo.discountType,
    discountValue,
    promotionDiscountValueRaw: promo.discountValue,
    translationKeyDiscountText: promo.translationKeyDiscountText,
    translationKeyMonthlyTab: promo.translationKeyMonthlyTab,
    translationKeyYearlyTab: promo.translationKeyYearlyTab,
    strikeThroughMonthlyPrice:
      promo.strikeThroughMonthlyPrice ?? promo.strike_through_monthly_price,
    discountText,
    planWidgetDiscountText,
    helper,
  };
}

const useDiscount = (): DiscountsByPlan | undefined => {
  const { t } = useTranslation();

  const country = useCustomerCountry() as AvailableCountries;

  const { data: { promotions = [] } = {} } = useQuery(
    SubscriptionsCacheKeys.Promotions,
    () => getPromotions(country),
    { staleTime: 5 * 60 * 1000 },
  );

  const discountsByPlan = useMemo(() => {
    if (!promotions?.length) return undefined;

    return promotions.reduce(
      (acc: DiscountsByPlan, promo: PromotionFromAPI) => {
        acc[promo.planInternalName] = mapPromotionToDiscount(promo, t);
        return acc;
      },
      {} as DiscountsByPlan,
    );
  }, [promotions, t]);

  return discountsByPlan;
};

export default useDiscount;
